import type PartnerService from 'tio-employee/services/partner';
import type EmployeeService from 'tio-employee/services/employee';
import type SessionContextService from 'tio-employee/services/session-context';
import type { BenefitFlags } from 'tio-employee/utils/beta/benefit-rows';

export interface BenefitFlagSources {
  partner: PartnerService;
  employee: EmployeeService;
  sessionContext: SessionContextService;
  /** The authenticated route's wellness payload; a `D-5` train hides the wellness row. */
  wellnessData: Record<string, unknown> | null | undefined;
}

/**
 * Reads the visibility flags the authenticated shell hands the side nav, so a
 * page can gate its own benefit list on exactly the same inputs.
 *
 * @param sources the partner, employee and session-context services plus the wellness payload
 * @returns the flags `benefitRows` consumes
 */
export function benefitFlags({
  partner,
  employee,
  sessionContext,
  wellnessData,
}: BenefitFlagSources): BenefitFlags {
  const roles = sessionContext.rolesForCurrentCompany;
  const isTaOnly = Boolean(partner.isTaOnly || employee.isTaOnlyEmployee);
  const plan = employee.current.plan;

  return {
    isTaOnly,
    isParticipant: roles.some((role) => role.relationshipType === 'PARTICIPANT') && !isTaOnly,
    isFamilyMember: Boolean(sessionContext.isFamilyMember),
    showTuitionAssistance: Boolean(partner.company?.taEnabled),
    showWellness: (wellnessData as { wellnessTrain?: string } | null)?.wellnessTrain !== 'D-5',
    showPslf: Boolean(partner.isPslfEnabled) && !employee.hasRogueFein,
    showSlr: Boolean(plan && plan.cadence !== 'NEVER'),
    showSyf: Boolean(partner.isSyfEnabled),
    showIdr: Boolean(partner.isIdrEnabled),
    isLpnEnabled: lpnEnabled(partner),
  };
}

function lpnEnabled(partner: PartnerService): boolean {
  const { lpn } = partner.companySettings;
  if (lpn && 'isEnabled' in lpn) {
    return lpn.isEnabled as boolean;
  }
  return false;
}
